/**
 * tierCostEstimator — approximate credit cost per quality tier for a given
 * video duration. Rates come from the product spec (Mar 2026, see the
 * cost-table reference image attached to the request).
 *
 *                      Economy   Standard   Premium
 *      15 seconds        500        800       1350
 *      30 seconds        830       1430       2530
 *      45 seconds       1155       2055       3705
 *      60 seconds       1480       2680       4880
 *      75 seconds       1805       3305       6055
 *      90 seconds       2130       3930       7230
 *
 * For durations between/outside the table we linearly interpolate (and
 * extrapolate) using the two nearest reference points. The spec's rates
 * are already near-linear so a straight-line extension stays accurate at
 * ±120 seconds without needing a bespoke curve.
 */

export const TIER_COST_TABLE = [
    { seconds: 15, economy:  500, standard:  800, premium: 1350 },
    { seconds: 30, economy:  830, standard: 1430, premium: 2530 },
    { seconds: 45, economy: 1155, standard: 2055, premium: 3705 },
    { seconds: 60, economy: 1480, standard: 2680, premium: 4880 },
    { seconds: 75, economy: 1805, standard: 3305, premium: 6055 },
    { seconds: 90, economy: 2130, standard: 3930, premium: 7230 },
];

const VALID_TIERS = ['economy', 'standard', 'premium'];

/**
 * Parse a project `duration` value into a positive integer number of seconds.
 * Accepts raw numbers ("60"), formatted strings ("60 seconds", "60s", "60 ")
 * and the Project Setup wizard's canonical values. Returns null on garbage.
 */
export const parseDurationSeconds = (duration) => {
    if (duration === null || duration === undefined) return null;
    if (typeof duration === 'number' && Number.isFinite(duration)) {
        return duration > 0 ? Math.round(duration) : null;
    }
    const s = String(duration).trim();
    if (!s) return null;
    const m = s.match(/-?\d+(?:\.\d+)?/);
    if (!m) return null;
    const n = parseFloat(m[0]);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.round(n);
};

/**
 * Return the approximate credit cost for `(durationSeconds, tier)`.
 * Linearly interpolates between adjacent table rows; extrapolates linearly
 * outside the table. Returns null for malformed input.
 */
export const estimateTierCost = (durationSeconds, tier) => {
    if (!VALID_TIERS.includes(tier)) return null;
    const d = typeof durationSeconds === 'number'
        ? durationSeconds
        : parseDurationSeconds(durationSeconds);
    if (!Number.isFinite(d) || d <= 0) return null;

    const rows = TIER_COST_TABLE;
    // Exact match
    const exact = rows.find(r => r.seconds === d);
    if (exact) return exact[tier];

    // Below the first row → linearly extrapolate using the first two rows.
    if (d < rows[0].seconds) {
        const a = rows[0], b = rows[1];
        const slope = (b[tier] - a[tier]) / (b.seconds - a.seconds);
        const value = a[tier] + slope * (d - a.seconds);
        return Math.max(0, Math.round(value));
    }
    // Above the last row → extrapolate using the last two rows.
    if (d > rows[rows.length - 1].seconds) {
        const a = rows[rows.length - 2], b = rows[rows.length - 1];
        const slope = (b[tier] - a[tier]) / (b.seconds - a.seconds);
        const value = b[tier] + slope * (d - b.seconds);
        return Math.max(0, Math.round(value));
    }
    // Interpolate between two bracketing rows.
    for (let i = 0; i < rows.length - 1; i++) {
        const a = rows[i], b = rows[i + 1];
        if (d >= a.seconds && d <= b.seconds) {
            const t = (d - a.seconds) / (b.seconds - a.seconds);
            const value = a[tier] + t * (b[tier] - a[tier]);
            return Math.max(0, Math.round(value));
        }
    }
    return null;
};

/** Format a credit count for display, e.g. 1480 → "1,480". */
export const formatCredits = (n) => {
    if (!Number.isFinite(n)) return '—';
    return Math.round(n).toLocaleString('en-US');
};
